import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useState, useRef, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Select } from '@ge/components/select';
import { FilterOptions, DateRange } from '@ge/feat-inspections/models/date-filter-options';
import { lastThreeMonths, lastSixMonths } from '@ge/feat-inspections/util';
import { DateTimeFormats } from '@ge/models/constants';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { Breakpoints } from '@ge/web-client/src/assets/styles/mixins';

const StyledHeader = styled.div`
  height: 25px;
  width: calc(100% - 44px);
  padding: 0 20px;
  display: flex;
  align-items: center;
`;

const StyledSelect = styled(Select)`
  .select__control {
    border: none;
  }
`;

const CustomDateRange = styled.div`
  margin-left: 10px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'auto')};
  display: flex;
  align-items: center;
  margin-right: 10px;
  span {
    font-size: 14px;
  }
  ${Breakpoints.tabletDown`
      span {
        font-size: 11px;
        max-width: 74px;
      }
      > div {
        flex: 1;
      }
  `}
`;

const CalendarIcon = styled(Icon).attrs(({ theme }) => ({
  size: 12,
  color: theme.analyze.header.calendarIconColor,
  icon: Icons.CALENDAR,
}))`
  margin-left: 6px;
  vertical-align: initial;
`;

export const DateFilter = ({ onChange, filterDate }) => {
  const { t } = useTranslation(['inspections.filter']);
  const datePickerRef = useRef();

  const [workingDateRange, setWorkingDateRange] = useState(() => {
    const currentDate = new Date();
    const endDate = currentDate;
    const startDate = currentDate;
    return { startDate, endDate };
  });
  const [oldDateRange, setOldDateRange] = useState(filterDate);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { setIsCustomApplied } = useContext(EntityDetailsContext);

  const handleCustomDateRange = useCallback(() => {
    const { startDate, endDate } = workingDateRange;
    const payloadFormat = DateTimeFormats.CREW_TIMING;

    if (startDate && endDate) {
      const payload = {
        endDate: dayjs(endDate).format(payloadFormat),
        startDate: dayjs(startDate).format(payloadFormat),
        range: DateRange.CUSTOM,
      };

      datePickerRef.current.setOpen(false);
      setOldDateRange(payload);
      onChange(payload);
      setIsCustomApplied(true);
    }
  }, [workingDateRange, onChange, setIsCustomApplied]);

  const handleDateRange = useCallback(
    ({ value: range }) => {
      const payloadFormat = DateTimeFormats.CREW_TIMING;
      if (range === DateRange.CUSTOM) {
        datePickerRef.current.setOpen(true);
        setCalendarOpen(true);
        const payload = {
          endDate: oldDateRange.endDate,
          startDate: oldDateRange.startDate,
          range: range,
        };
        onChange(payload);
      } else if (range === DateRange.LAST_3_MONTHS) {
        const { endDate, startDate } = lastThreeMonths();
        const payload = {
          endDate: dayjs(endDate).format(payloadFormat),
          startDate: dayjs(startDate).format(payloadFormat),
          range: range,
        };
        setOldDateRange(payload);
        onChange(payload);
      } else if (range === DateRange.LAST_6_MONTHS) {
        const { endDate, startDate } = lastSixMonths();
        const payload = {
          endDate: dayjs(endDate).format(payloadFormat),
          startDate: dayjs(startDate).format(payloadFormat),
          range: range,
        };
        setOldDateRange(payload);
        onChange(payload);
      }
    },
    [onChange, oldDateRange],
  );

  const handleDatePickerChange = useCallback(([startDate, endDate]) => {
    setWorkingDateRange((prev) => ({ ...prev, startDate, endDate }));
  }, []);

  const handleDatePickerClose = useCallback(() => {
    setCalendarOpen(false);
    setWorkingDateRange({
      endDate: new Date(),
      startDate: new Date(),
    });
  }, []);

  const dateSelectOptions = useMemo(() => {
    return FilterOptions.map((filter) => ({
      value: filter.key,
      label: t(filter.a11yKey, filter.label),
    }));
  }, [t]);

  const renderDates = useCallback(() => {
    const { startDate, endDate } = filterDate;

    return `${dayjs(startDate).format(DateTimeFormats.FILTER_CUSTOM_DATE)} - ${dayjs(
      endDate,
    ).format(DateTimeFormats.FILTER_CUSTOM_DATE)}`;
  }, [filterDate]);

  const renderCustomDates = useCallback(() => {
    const placeholderText = t('filters.select_date', 'Select Date');
    const startDate = workingDateRange.startDate
      ? dayjs(workingDateRange.startDate).format(DateTimeFormats.FILTER_CUSTOM_DATE)
      : placeholderText;
    const endDate = workingDateRange.endDate
      ? dayjs(workingDateRange.endDate).format(DateTimeFormats.FILTER_CUSTOM_DATE)
      : placeholderText;

    return `${startDate} - ${endDate}`;
  }, [t, workingDateRange]);

  return (
    <StyledHeader>
      <StyledSelect
        value={dateSelectOptions.find((option) => option.value === filterDate.range)}
        onChange={handleDateRange}
        options={dateSelectOptions}
        transparent
        minWidth={115}
      />
      {!(filterDate.range === DateRange.CUSTOM) && (
        <CustomDateRange clickable={false}>
          <span>{renderDates()}</span>
        </CustomDateRange>
      )}
      <DatePicker
        startDate={workingDateRange.startDate}
        endDate={workingDateRange.endDate}
        selectsRange
        showWeekNumbers
        maxDate={new Date()}
        calendarStartDay={1}
        ref={datePickerRef}
        customInput={
          filterDate.range === DateRange.CUSTOM ? (
            <CustomDateRange clickable>
              {calendarOpen ? <span>{renderCustomDates()}</span> : <span>{renderDates()}</span>}
              <div>
                <CalendarIcon />
              </div>
            </CustomDateRange>
          ) : (
            <div />
          )
        }
        shouldCloseOnSelect={false}
        popperPlacement="bottom-start"
        onApplyClick={handleCustomDateRange}
        onCalendarClose={handleDatePickerClose}
        onCalendarOpen={() => setCalendarOpen(true)}
        onChange={handleDatePickerChange}
      />
    </StyledHeader>
  );
};

DateFilter.propTypes = {
  onChange: PropTypes.func,
  filterDate: PropTypes.object,
};

DateFilter.defaultProps = {
  onChange: () => {},
  filterDate: {},
};
